import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { productService } from 'services/product.service';
import type { BundlePromotionConfig } from 'utils/bundlePromotions';

type BundlePromotionContextValue = {
  config: BundlePromotionConfig | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
};

const BundlePromotionContext = createContext<BundlePromotionContextValue | undefined>(undefined);

export const BundlePromotionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [config, setConfig] = useState<BundlePromotionConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await productService.getBundlePromotion();
      setConfig(data);
    } catch (err) {
      setConfig(null);
      setError(err instanceof Error ? err.message : 'Failed to load promotion');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const value = useMemo(
    () => ({ config, loading, error, refresh }),
    [config, loading, error, refresh],
  );

  return (
    <BundlePromotionContext.Provider value={value}>
      {children}
    </BundlePromotionContext.Provider>
  );
};

export function useBundlePromotion(): BundlePromotionContextValue {
  const context = useContext(BundlePromotionContext);
  if (!context) {
    throw new Error('useBundlePromotion must be used within BundlePromotionProvider');
  }
  return context;
}
